import { CoreLayoutView, TCoreLayoutStyleProps } from '@core/widgets';
import { TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { TVerticalLayoutOption } from '../../types/options';
import styles from './css/verticalLayout.module.css';

const toLine = (value?: string): TCoreLayoutStyleProps['lineY'] => (value ? (value.replace(/-/g, '_') as TCoreLayoutStyleProps['lineY']) : undefined);

export type TVerticalLayoutViewProps = {
    /** Value for the `data-test` attribute (widget uid in server-driven scenarios). */
    uid?: string;
    /** Pre-resolved visibility state (derived from useBehavior one level up). */
    isHide?: boolean;
    /** Horizontal alignment of children, mapped to CoreLayout `lineX`. */
    alignment?: TVerticalLayoutOption['alignment'];
    /** Vertical arrangement of children, mapped to CoreLayout `lineY`. */
    arrangement?: TVerticalLayoutOption['arrangement'];
    /** Server-driven theme style name forwarded to CoreLayoutView for theme resolution. */
    styleName?: string;
    children?: React.ReactNode;
} & Pick<TWidgetCommonOption, 'margin'>;

/**
 * Composable vertical layout building block for use inside complex widgets.
 * Owns only rendering logic — the wrapper element and the mapping of
 * alignment/arrangement options into CoreLayout style props.
 *
 * Accepts pre-resolved behavior props (isHide) and plain display values from
 * the parent. It NEVER receives a `widget` object — every value is an explicit
 * prop, resolved one level up (see VerticalLayoutWidget).
 */
const VerticalLayoutView = ({ uid, isHide, alignment, arrangement, styleName, margin, children }: TVerticalLayoutViewProps) => {
    const styleProps: TCoreLayoutStyleProps = {
        lineY: toLine(arrangement),
        lineX: alignment === 'center' ? 'center' : alignment === 'bottom' ? 'bottom' : 'stretch',
        margin,
    };

    return (
        <div data-test={uid} className={clsx('vertical-layout', styles.verticalLayout)}>
            <CoreLayoutView uid={'verticalLayoutInner_' + uid} styleProps={styleProps} isHide={isHide} styleName={styleName}>
                {children}
            </CoreLayoutView>
        </div>
    );
};

export { VerticalLayoutView };
